
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MapPin, PoundSterling, ArrowLeft, Zap } from "lucide-react";
import { useState, useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import PodPricing from "./PodPricing";
import BookingModal from "./BookingModal";
import { supabase } from "../integrations/supabase/client";

interface PodData {
  name: string;
  address: string;
  price_per_minute: string;
  in_use: boolean;
}

interface PodDetailsProps {
  pod: PodData;
}

const PodDetails = ({ pod }: PodDetailsProps) => {
  const navigate = useNavigate();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [podStatus, setPodStatus] = useState<{ in_use: boolean } | null>(null);

  // Set up real-time subscription to listen for pod status changes
  useEffect(() => {
    const podId = window.location.pathname.split('/').pop();
    if (!podId) return;

    const channel = supabase
      .channel('pod-status-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pods'
        },
        (payload) => {
          console.log('Pod status change received:', payload);
          // Check if this update is for the current pod
          if (payload.new && payload.new.id === podId) {
            setPodStatus({ in_use: payload.new.in_use });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleUseNow = () => {
    setIsBookingModalOpen(true);
  };

  // Use real-time status if available, otherwise fall back to initial pod data
  const currentInUse = podStatus?.in_use ?? pod.in_use;

  return (
    <div className="min-h-screen bg-[#f9f8f5]">
      <Header />
      
      <main className="pt-20">
        <div className="max-w-[800px] mx-auto px-4 desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#374151] hover:text-[#374151]/80 mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>

            <motion.div 
              className="bg-white rounded-lg shadow-lg p-8"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.4, 
                ease: [0.16, 1, 0.3, 1]
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mb-8"
              >
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl desktop:text-4xl laptop:text-3xl tablet:text-2xl mobile:text-xl font-bold text-[#374151]">
                    {pod.name}
                  </h1>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    currentInUse 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {currentInUse ? 'In Use' : 'Available'}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-6 mb-8"
              >
                <div className="flex items-start gap-4">
                  <MapPin className="text-[#374151] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#374151] mb-1">Location</h3>
                    <p className="text-gray-700 text-lg">{pod.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <PoundSterling className="text-[#374151] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#374151] mb-1">Pricing</h3>
                    <div className="text-gray-700 text-lg">
                      <PodPricing pricePerMinute={pod.price_per_minute} />
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Zap className="text-[#374151] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-[#374151] mb-1">Status</h3>
                    <p className="text-gray-700 text-lg">
                      {currentInUse ? 'Currently occupied' : 'Ready to use'}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="pt-6 border-t border-gray-200"
              >
                <button
                  onClick={handleUseNow}
                  disabled={currentInUse}
                  className={`w-full py-4 px-8 rounded-lg font-semibold text-lg transition-colors ${
                    currentInUse
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
                >
                  {currentInUse ? 'Pod In Use' : 'Use Now'}
                </button>
                <p className="text-center text-gray-500 text-sm mt-2">
                  {currentInUse ? 'This pod is currently occupied' : 'Start your session instantly'}
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </main>

      <Footer />

      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        podId={window.location.pathname.split('/').pop() || ''}
        podName={pod.name}
      />
    </div>
  );
};

export default PodDetails;
